(ns app.front.ui.expandable
  (:require [app.front.util :as futil]
            [core.util.util :as util]
            [reagent.core :as r]))

(defn handle-on-toggle [*requested-state transition-duration on-expand state]
  (let [collapsed? (:collapsed? state)]
    ;; If a callback is defined, call it after the transition is done to prevent stuttering
    (js/setTimeout #(when (and collapsed? on-expand) (on-expand)) transition-duration)
    (reset! *requested-state (-> state :collapsed? not))))

(defn expandable
  "Provide at least
   :toggle-state-fn (fn [new-state-bool])
   :state a map of {:collapsed? bool}

   component-fn is (fn [{:keys [expandable-container-props toggle-fn toggle-and-close-others-fn collapsed?]}]) where
       expandable-container-props is a function that produces or augments the reagent props map for the expandable
                                  content, i.e. (fn ([]) ([props]))"
  [{:keys [section-counter on-expand transition-duration transition-function state toggle-state-fn *toggle-fns]
    :or   {transition-duration 500}}
   component-fn]
  ;; Max-height needs to be removed in order for the component to be able to automatically grow as
  ;; the contents inside grow (there's no way nor need of knowing that inside this component). Max-height
  ;; is "removed" by setting it to "unset". Max-height is then re-set when the user clicks the component
  ;; header and before the animation starts. The transition atom is used to track whether we're in middle
  ;; of a transition, to prevent dispatching the ::set-collapsed event too early.
  (r/with-let [*requested-state (r/atom nil)                ; nil = no transition requested, true = collapsing, false = expanding
               *child-height    (r/atom (fn [] 0))          ; store the height of the child, used to set max-height when animating
               on-toggle-fn     (futil/make-callback-fn handle-on-toggle *requested-state transition-duration)]
    (let [collapsed?                 (:collapsed? state)
          toggle-fn                  (on-toggle-fn on-expand state)
          toggle-and-close-others-fn #(when (seq @*toggle-fns)
                                        (doseq [tf (conj @*toggle-fns
                                                         (when collapsed? toggle-fn))]
                                          (when tf (tf))))
          on-transition-end          #(reset! *requested-state nil) ; is either collapsing or expanding
          transitioning?             (-> @*requested-state nil? not)
          set-child-height           (fn [el]
                                       (when el
                                         (reset! *child-height (fn [] (.-scrollHeight el))))
                                       ;; ref callback fn must not return a value
                                       nil)
          max-height                 (cond
                                       collapsed? "0px"
                                       transitioning? (str (@*child-height) "px")
                                       :else "unset")]

      ;; When not collapsed, store toggle-fn to atom to enable other sections to close this one in accordion implementations
      (when (and *toggle-fns section-counter)
        (swap! *toggle-fns assoc section-counter (when-not collapsed? toggle-fn)))

      (when (and transitioning? (not= @*requested-state collapsed?))
        ;; This is a bit unorthodox, but we're dispatching an event from render so that the transition
        ;; does not get interrupted by the changes made by the event.
        (toggle-state-fn @*requested-state))
      (component-fn
        (util/mmap {:expandable-container-props (fn [& [props]]
                                                  (r/merge-props
                                                    props
                                                    {:on-transition-end on-transition-end
                                                     :aria-hidden       collapsed?
                                                     ;; disable keyboard navigation for contents when collapsed:
                                                     :inert             (when collapsed? "true")
                                                     ;; save actual clientHeight to do the trick with transformation
                                                     :ref               set-child-height
                                                     :style             (merge {:transition-property "max-height"
                                                                                :transition-duration (str transition-duration "ms")
                                                                                :max-height          max-height}
                                                                               (when transition-function
                                                                                 {:transition-timing-function transition-function})
                                                                               (when (or collapsed? transitioning?)
                                                                                 {:contain "layout"}))
                                                     :class             (when (or collapsed? transitioning?)
                                                                          "overflow-hidden")}))}
                   toggle-fn
                   toggle-and-close-others-fn
                   collapsed?)))))
